
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { SupplierModel, ISupplierProductDisplay } from '@/models/supplier';
import { IPurchaseOrderItem } from '@/models/purchase_order';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import { excludeAlreadyOnPo, AddProductsFetchGate } from '@/components/purchase-order/add_products_dialog_helpers';
import { EventBus } from '@/main';

@Component
export default class AddProductsDialog extends Vue {
  @Prop({ type: Boolean, required: true }) readonly dialog!: boolean;
  @Prop({ type: Number, default: null }) readonly supplierId!: number | null;
  @Prop({ type: String, default: '' }) readonly supplierName!: string;
  // US-090: variant ids already on the PO. Both pages hold their lines in different
  // shapes (IPurchaseOrderItem vs table rows), and ids are all this dialog needs.
  @Prop({ type: Array, default: () => [] }) readonly excludeVariantIds!: Array<number | string>;

  private searchQuery = '';
  private selectedProducts: ISupplierProductDisplay[] = [];
  // US-090: rows exactly as fetched. Exclusion is applied at render time (see
  // availableProducts below) so adding a product removes it from the list without
  // costing another full-catalog fetch.
  private fetchedProducts: ISupplierProductDisplay[] = [];
  private fetchGate = new AddProductsFetchGate();
  private loading = false;
  private adding = false;
  private totalProducts = 0;
  private supplier: SupplierModel | null = null;
  private fetchError = '';

  private tableOptions: any = {
    page: 1,
    itemsPerPage: 25,
    sortBy: [],
    sortDesc: [],
  };

  private headers = [
    { text: '', value: 'image', sortable: false, width: '56px' },
    { text: 'Product', value: 'productDetails', sortable: false },
    { text: 'Unit Cost', value: 'unit_cost', sortable: true, width: '120px', align: 'right' },
  ];

  @Watch('dialog')
  onDialogChange(val: boolean) {
    if (!val) { return; }
    // US-090: the picked-products selection is always per-visit, but the fetched
    // catalog is reused when the dialog reopens for the same supplier — refetching it
    // costs a full SupplierProduct scan plus an OLAP query over the whole catalog.
    this.selectedProducts = [];
    if (this.fetchGate.onOpen(this.supplierId)) {
      this.resetState();
      this.initSupplier();
    }
  }

  @Watch('tableOptions', { deep: true })
  onTableOptionsChange() {
    // US-090: fetchProducts(true) resets the page, which wakes this watcher; without
    // the gate a search from any page but the first fired two identical requests.
    if (this.dialog && this.supplier && this.fetchGate.onOptionsChanged()) {
      this.fetchProducts();
    }
  }

  // US-090: the catalog is cached across reopens, so give the user a way to pull it
  // again after linking products to the supplier in another tab.
  private refreshProducts() {
    this.fetchGate.invalidate();
    this.onDialogChange(true);
  }

  get availableProducts(): ISupplierProductDisplay[] {
    return excludeAlreadyOnPo(this.fetchedProducts, this.excludeVariantIds);
  }

  // US-090: `server-items-length` is the server's unfiltered total while `items` is
  // exclusion-filtered, so a page can render short — or empty, once enough of it is on
  // the PO — while the footer still counts those rows. Making the total exclusion-aware
  // needs the endpoint to know the PO (US-091); until then keep the count honest to the
  // server and name the one case that otherwise reads as a broken dialog, since
  // "No products assigned to this supplier" would be plainly wrong here.
  get allOnThisPageExcluded(): boolean {
    return this.fetchedProducts.length > 0 && this.availableProducts.length === 0;
  }

  private resetState() {
    this.supplier = null;
    this.searchQuery = '';
    this.selectedProducts = [];
    this.fetchedProducts = [];
    this.totalProducts = 0;
    this.loading = false;
    this.adding = false;
    this.fetchError = '';
    this.tableOptions = {
      page: 1,
      itemsPerPage: 25,
      sortBy: [],
      sortDesc: [],
    };
  }

  private async initSupplier() {
    if (!this.supplierId) { return; }
    // US-090: fetchProductsOptimized only needs the id, and both call sites already
    // hold the loaded supplier — GET /suppliers/:id here was a wasted round trip.
    // fetchProducts() owns the error handling and the loading flag from here.
    this.supplier = new SupplierModel({ id: this.supplierId });
    await this.fetchProducts();
  }

  private async fetchProducts(resetPage = false) {
    if (!this.supplier) return;

    if (resetPage && this.tableOptions.page !== 1) {
      this.fetchGate.suppressNextWatch();
      this.tableOptions.page = 1;
    }

    this.loading = true;
    try {
      const result = await this.supplier.fetchProductsOptimized({
        page: this.tableOptions.page,
        limit: this.tableOptions.itemsPerPage,
        search: this.searchQuery || '',
        sortBy: this.tableOptions.sortBy?.[0] || '',
        sortOrder: this.tableOptions.sortDesc?.[0] ? 'desc' : 'asc',
      });

      this.fetchedProducts = result.products;
      this.totalProducts = result.pagination.total;
      this.fetchError = '';
    } catch (error: any) {
      // US-090: onOpen() records the supplier before the request resolves, so without
      // this a failed load stays "cached" — closing and reopening for the same supplier
      // would skip the refetch and leave the user staring at the stale error alert.
      this.fetchGate.invalidate();
      this.fetchError = `Failed to fetch products: ${error.message || 'Unknown error'}`;
      EventBus.$emit('show-snackbar', this.fetchError, 'error');
      console.error('Error fetching supplier products:', error);
    } finally {
      this.loading = false;
    }
  }

  private onSearchClear() {
    // Clearing is an explicit action, so reset the list to the full catalog.
    this.searchQuery = '';
    this.fetchProducts(true);
  }

  private formatCurrency(value: number): string {
    return CurrencyHelper.formatCurrency(value);
  }

  private addSelectedProducts() {
    if (this.selectedProducts.length === 0) return;

    // Convert supplier products to PO item format
    const newItems: IPurchaseOrderItem[] = this.selectedProducts.map(product => ({
      variantId: Number(product.variant_id),
      variantImage: product.image || '',
      productName: product['Item[Item Description]'] || '',
      variant: product.variant || '',
      sku: product.product_sku || '',
      supplierSku: product.supplier_sku || '',
      orderedQuantity: product['[min_order_quantity]'] || 1,
      receivedQuantity: 0,
      unitPrice: product.unit_cost || 0,
      totalPrice: (product['[min_order_quantity]'] || 1) * (product.unit_cost || 0),
      _action: 'add',
    }));

    this.$emit('products-added', newItems);
    // US-090: the Create-PO page builds its own table rows (cost ladder, MOQ-seeded
    // quantity) from the raw supplier-product records, so hand those over too. Edit-PO
    // keeps consuming 'products-added' above.
    this.$emit('products-selected', this.selectedProducts.slice());
    this.closeDialog();
  }

  private closeDialog() {
    this.$emit('update:dialog', false);
  }
}
